import { useState } from "react";
import { Link } from "react-router-dom";
import QtyStepper from "./QtyStepper";
import { availableForSku } from "../lib/stockGuard";
import styles from "./CartLine.module.css";

// One row in the cart. Three columns:
//   [thumb] [name + color/size + qty stepper] [edit/save/remove + amount]
// Edit toggles a colored swatch view; the actual color change is a
// secondary flow that links into the PDP.
export default function CartLine({ line, onQty, onRemove, onSaveForLater, formatAmount }) {
  const [editing, setEditing] = useState(false);
  const meta = line.meta || {};
  const unit = line.unit || 0;
  const amount = unit * (line.qty || 0);
  const productHref = meta.parentId
    ? `/product/${encodeURIComponent(meta.parentId)}`
    : `/product/${encodeURIComponent((line.sku || "").split("-")[0].toLowerCase())}`;

  return (
    <article className={styles.row}>
      <Link to={productHref} className={styles.thumbLink}>
        {meta.image ? (
          <img src={meta.image} alt="" className={styles.thumb} />
        ) : (
          <div className={styles.thumbPlaceholder} />
        )}
      </Link>

      <div className={styles.body}>
        <Link to={productHref} className={styles.name}>
          {meta.name || line.sku}
        </Link>
        {unit > 0 && <div className={styles.unit}>{formatAmount(unit)}</div>}

        <dl className={styles.attrs}>
          {meta.color && (
            <div className={styles.attr}>
              <dt>Color:</dt>
              <dd>
                <span className={styles.swatch} style={swatchStyle(meta.color)} aria-hidden />
                {meta.color.name}
              </dd>
            </div>
          )}
          {meta.size && (
            <div className={styles.attr}>
              <dt>Size:</dt>
              <dd>{meta.size}</dd>
            </div>
          )}
          <div className={styles.attr}>
            <dt>SKU:</dt>
            <dd className={styles.mono}>{line.sku}</dd>
          </div>
        </dl>

        <div className={styles.qtyRow}>
          <span className={styles.qtyLabel}>Quantity</span>
          <QtyStepper
            value={line.qty || 0}
            max={availableForSku(line.sku) ?? undefined}
            onChange={(n) => onQty?.(n)}
            size="sm"
          />
        </div>
        {(() => {
          const stock = availableForSku(line.sku);
          if (stock == null) return null;
          if ((line.qty || 0) >= stock) {
            return (
              <div className={styles.stockWarn}>
                Reached available stock ({stock}). Reduce qty to continue.
              </div>
            );
          }
          if (stock <= 5) {
            return <div className={styles.stockNote}>Only {stock} left in stock.</div>;
          }
          return null;
        })()}
      </div>

      <div className={styles.actions}>
        <Link to={productHref} className={styles.btnPrimary}>Edit</Link>
        <button type="button" className={styles.btnDark} onClick={onSaveForLater}>
          Save for Later
        </button>
        <button type="button" className={styles.btnDark} onClick={onRemove}>
          Remove
        </button>
        {amount > 0 && (
          <div className={styles.amountBlock}>
            <span className={styles.amountLabel}>Amount:</span>
            <strong className={styles.amount}>{formatAmount(amount)}</strong>
          </div>
        )}
      </div>
    </article>
  );
}

function swatchStyle(c) {
  if (!c) return { background: "#ccc" };
  if (c.image) return { backgroundImage: `url(${c.image})`, backgroundSize: "cover" };
  return { background: c.hex || "#ccc" };
}
